import { ASTRIX_SYMBOL } from "../../constants";
import { IPassengerInfo, IRideDto, IStationInfo } from "../../dto";
import {
  DirectionEnum,
  StationOptionsEnum,
  TRestrictedDirection,
} from "../../enums";
import type {
  FEAvailableSystemParamMetadata,
  IDropdownItem,
  IPassengerForStationDto,
  TRideStationOptions,
} from "../../models";
import { unique } from "../array.helpers";
import { isValidPositiveNumber } from "../numbers.helpers";
import {
  isStationRouteWaypointStation,
  preserveRideRoutingWaypointsForPassengerStationChange,
} from "../station-route";
import {
  pickEffectiveSystemParamsFromCompanyStore,
  type IFeSystemParamsBySiteStore,
} from "../system-params-store.helpers";

export const getRideStationOptionsFromSystemParams = (
  metadata?: Pick<
    FEAvailableSystemParamMetadata,
    "stationOptionA" | "stationOptionB"
  > | null,
): TRideStationOptions => ({
  // Unset A/B stay MeetingPoint on the ride screen; booking create uses Home instead.
  pickUp: metadata?.stationOptionA ?? StationOptionsEnum.MeetingPoint,
  dropOff: metadata?.stationOptionB ?? StationOptionsEnum.MeetingPoint,
});

export const resolvePassengerRideStationOptions = ({
  passengerDto,
  fallbackStationOptions,
  companyParams,
  paramsBySite,
}: {
  passengerDto: IPassengerForStationDto;
  fallbackStationOptions: TRideStationOptions;
  companyParams?: FEAvailableSystemParamMetadata;
  paramsBySite?: IFeSystemParamsBySiteStore;
}): TRideStationOptions => {
  // Dedicated site row when the passenger has a siteId; otherwise keep the ride's options.
  if (!isValidPositiveNumber(passengerDto.siteId)) {
    return fallbackStationOptions;
  }

  const siteParams = pickEffectiveSystemParamsFromCompanyStore({
    companyParams,
    paramsBySite,
    siteId: passengerDto.siteId,
  });

  return getRideStationOptionsFromSystemParams(siteParams);
};

export const resolveDestinationStationTitle = ({
  passengerDto,
  stationOption,
}: {
  passengerDto: IPassengerForStationDto;
  stationOption: StationOptionsEnum;
}): string | undefined => {
  // Destination 1 wins when set (RYDE-8181); the option chain is only the fallback.
  if (passengerDto?.destinationStation1Id) {
    return passengerDto.destinationStation1Title;
  }

  if (stationOption === StationOptionsEnum.AltMeetingPoint) {
    if (passengerDto?.destinationStation3Id) {
      return passengerDto.destinationStation3Title;
    }

    if (passengerDto?.destinationStation2Id) {
      return passengerDto.destinationStation2Title;
    }
  }

  if (
    stationOption === StationOptionsEnum.MeetingPoint &&
    passengerDto?.destinationStation2Id
  ) {
    return passengerDto.destinationStation2Title;
  }

  return passengerDto.destinationStation1Title;
};

const getDestinationStationTitleByOption = ({
  passengerDto,
  stationOptions,
  direction,
}: {
  passengerDto: IPassengerForStationDto;
  direction: TRestrictedDirection;
  stationOptions: TRideStationOptions;
}): string => {
  // Same-direction option as origin: pick-up does not read dropOff.
  const destinationOption =
    direction === DirectionEnum.PickUp
      ? stationOptions.pickUp
      : stationOptions.dropOff;
  return (
    resolveDestinationStationTitle({
      stationOption: destinationOption,
      passengerDto,
    }) || ASTRIX_SYMBOL
  );
};

const resolveSourceStationTitle = ({
  passengerDto,
  pickUpOption,
}: {
  passengerDto: IPassengerForStationDto;
  pickUpOption: StationOptionsEnum;
}): string | undefined => {
  if (pickUpOption === StationOptionsEnum.AltMeetingPoint) {
    if (passengerDto?.sourceStation3Id) {
      return passengerDto.sourceStation3Title;
    }

    if (passengerDto?.sourceStation2Id) {
      return passengerDto.sourceStation2Title;
    }

    return passengerDto.sourceStation1Title;
  }

  if (pickUpOption === StationOptionsEnum.MeetingPoint) {
    if (passengerDto?.sourceStation2Id) {
      return passengerDto.sourceStation2Title;
    }

    return passengerDto.sourceStation1Title;
  }

  return passengerDto.sourceStation1Title;
};

const getSourceStationTitleByOption = ({
  passengerDto,
  stationOptions,
  direction,
}: {
  passengerDto: IPassengerForStationDto;
  direction: TRestrictedDirection;
  stationOptions: TRideStationOptions;
}): string => {
  if (direction !== DirectionEnum.PickUp) {
    return passengerDto?.sourceStation1Title || ASTRIX_SYMBOL;
  }

  const pickUpOption = stationOptions.pickUp;
  return (
    resolveSourceStationTitle({ passengerDto, pickUpOption }) || ASTRIX_SYMBOL
  );
};

interface IGetPassengerDisplayLabelArguments {
  passengerDto: IPassengerForStationDto;
  direction: TRestrictedDirection;
  stationOptions: TRideStationOptions;
}

export const getPassengerDisplayLabel = ({
  passengerDto,
  stationOptions,
  direction,
}: IGetPassengerDisplayLabelArguments): string => {
  const firstName = passengerDto?.firstName || "";
  const lastName = passengerDto?.lastName || "";
  const isOtherSitePassenger = passengerDto?.isLimitedPermissionsSite;
  const passengerCode = passengerDto?.passengerCode
    ? `| #${passengerDto?.passengerCode}`
    : "";
  const sourceStationTitle = getSourceStationTitleByOption({
    passengerDto,
    stationOptions,
    direction,
  });
  const destinationStationTitle = getDestinationStationTitleByOption({
    passengerDto,
    stationOptions,
    direction,
  });

  return (
    `${firstName} ${lastName} ${passengerCode} ${isOtherSitePassenger ? "*" : ""}` +
    `   (${sourceStationTitle}-${destinationStationTitle})`
  );
};

interface INormalizePassengersDropdownItemsArguments {
  passengersList: IPassengerForStationDto[] | undefined;
  excludeIds?: number[];
  direction: TRestrictedDirection;
  stationOptions: TRideStationOptions;
  companyParams?: FEAvailableSystemParamMetadata;
  paramsBySite?: IFeSystemParamsBySiteStore;
}

export const normalizePassengersDropdownItems = ({
  passengersList,
  excludeIds,
  direction,
  stationOptions,
  companyParams,
  paramsBySite,
}: INormalizePassengersDropdownItemsArguments): IDropdownItem[] => {
  const list: IDropdownItem[] = [];
  passengersList?.forEach(passenger => {
    // Label each row from that passenger's site so picking one does not relabel the list.
    const passengerStationOptions = resolvePassengerRideStationOptions({
      passengerDto: passenger,
      fallbackStationOptions: stationOptions,
      companyParams,
      paramsBySite,
    });
    const label = getPassengerDisplayLabel({
      passengerDto: passenger,
      stationOptions: passengerStationOptions,
      direction,
    });

    const excludeIdsSet = excludeIds ? new Set(excludeIds) : null;
    if (
      !excludeIdsSet?.has(passenger.id) &&
      !list.some(item => item.label === label)
    ) {
      list.push({ value: passenger.id, label });
    }
  });

  return list;
};

export const sortStationsByPassengers = ({
  rideDto,
  stationsInfos,
  passengersInfos,
}: {
  rideDto: Partial<IRideDto>;
  stationsInfos?: IStationInfo[];
  passengersInfos?: IPassengerInfo[];
}): IStationInfo[] => {
  if (!passengersInfos?.length) {
    return stationsInfos || [];
  }

  if (!stationsInfos?.length) {
    return [];
  }

  const firstStation = stationsInfos[0];
  const lastStation = stationsInfos[stationsInfos.length - 1];

  const isFirstLocked = firstStation.isLocked === true;
  const isLastLocked = lastStation.isLocked === true;

  const stationsToSort = stationsInfos.slice(
    isFirstLocked ? 1 : 0,
    isLastLocked ? -1 : stationsInfos.length,
  );

  const stationIdsOrdered = unique([
    ...passengersInfos.filter(e => Number(e.from)).map(e => Number(e.from)),
    ...passengersInfos.filter(e => Number(e.to)).map(e => Number(e.to)),
  ]);

  const stationsToSortMap = new Map(stationsToSort.map(s => [s.id, s]));
  let stationsInfoOrdered = stationIdsOrdered
    .map(stationId => stationsToSortMap.get(stationId))
    .filter((x): x is IStationInfo => x !== undefined);

  const stationIdsOrderedSet = new Set(stationIdsOrdered);
  const stationsWithoutPassengers = stationsToSort.filter(
    station =>
      !stationIdsOrderedSet.has(station.id) &&
      !isStationRouteWaypointStation(station),
  );

  const areEmptyStationsAllowed =
    rideDto.courseId || rideDto.isRideWithEmptyStationsAllowed;

  if (stationsWithoutPassengers.length && areEmptyStationsAllowed) {
    const stationsWithPassengers = stationsInfoOrdered.filter(station =>
      stationIdsOrderedSet.has(station.id),
    );

    stationsInfoOrdered =
      stationsInfoOrdered.length > 1
        ? [
            ...stationsInfoOrdered.slice(0, -1),
            ...stationsWithoutPassengers,
            stationsWithPassengers[stationsWithPassengers.length - 1],
          ]
        : [...stationsWithPassengers, ...stationsWithoutPassengers];
  }

  if (isFirstLocked) {
    stationsInfoOrdered = [firstStation, ...stationsInfoOrdered];
  }

  if (isLastLocked) {
    stationsInfoOrdered = [...stationsInfoOrdered, lastStation];
  }

  // Compare against rideDto stations (pre-merge), not stationsInfos which already includes newly added stops.
  return preserveRideRoutingWaypointsForPassengerStationChange({
    previousStations: rideDto.metadata?.stationsInfo ?? stationsInfos,
    nextStations: stationsInfoOrdered,
  });
};

interface IGetPassengerStationIdByStationOptionArguments {
  passengerDto: IPassengerForStationDto;
  stationOption: StationOptionsEnum;
}

export const getPassengerSourceStationsIds = ({
  passengerDto,
  stationOption,
}: IGetPassengerStationIdByStationOptionArguments): number | undefined => {
  switch (stationOption) {
    case StationOptionsEnum.AltMeetingPoint:
      return (
        passengerDto?.sourceStation3Id ||
        passengerDto?.sourceStation2Id ||
        passengerDto.sourceStation1Id
      );
    case StationOptionsEnum.MeetingPoint:
      return passengerDto.sourceStation2Id || passengerDto.sourceStation1Id;
    default:
      return passengerDto.sourceStation1Id;
  }
};

export const getPassengerDestinationStationIds = ({
  passengerDto,
  stationOption,
}: IGetPassengerStationIdByStationOptionArguments): number | undefined => {
  // Destination 1 wins when set (RYDE-8181); the option chain is only the fallback.
  if (passengerDto?.destinationStation1Id) {
    return passengerDto.destinationStation1Id;
  }

  switch (stationOption) {
    case StationOptionsEnum.AltMeetingPoint:
      return (
        passengerDto?.destinationStation3Id ||
        passengerDto?.destinationStation2Id ||
        passengerDto.destinationStation1Id
      );
    case StationOptionsEnum.MeetingPoint:
      return (
        passengerDto.destinationStation2Id || passengerDto.destinationStation1Id
      );
    default:
      return passengerDto.destinationStation1Id;
  }
};

export const getSinglePassengerSourceStationsIds = (
  passenger: IPassengerForStationDto | undefined,
): number[] => {
  if (!passenger) {
    return [];
  }

  const ids = new Set<number>();

  if (passenger.sourceStation1Id) {
    ids.add(passenger.sourceStation1Id);
  }

  if (passenger.sourceStation2Id) {
    ids.add(passenger.sourceStation2Id);
  }

  if (passenger.sourceStation3Id) {
    ids.add(passenger.sourceStation3Id);
  }

  return Array.from(ids);
};

export const getSinglePassengerDestinationStationIds = (
  passenger: IPassengerForStationDto | undefined,
): number[] => {
  if (!passenger) {
    return [];
  }

  const ids = new Set<number>();

  if (passenger.destinationStation1Id) {
    ids.add(passenger.destinationStation1Id);
  }

  if (passenger.destinationStation2Id) {
    ids.add(passenger.destinationStation2Id);
  }

  if (passenger.destinationStation3Id) {
    ids.add(passenger.destinationStation3Id);
  }

  return Array.from(ids);
};
